/**
 * KpiCockpitPage — /admin/kpi-cockpit
 *
 * Founder's daily "who's actually working" view. Studio-wide heatmap of
 * measured artist effort, sourced from VOS Studio Agent activity samples.
 *
 * Three lenses:
 *   • Top stat bar: total active hours, idle hours, Blender hours.
 *   • Per-artist table: active/idle/Blender minutes + active ratio.
 *   • Day×Artist heat-map at the bottom for at-a-glance patterns.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Activity, Clock, Cpu, RefreshCw, Filter, TrendingUp, Users,
} from 'lucide-react';

function fmtH(min) {
  if (!min) return '0';
  if (min < 60) return `${Math.round(min)}m`;
  return `${(min / 60).toFixed(1)}h`;
}

function ratioColor(r) {
  if (r >= 0.8) return 'bg-emerald-500';
  if (r >= 0.6) return 'bg-amber-500';
  if (r >= 0.4) return 'bg-orange-500';
  return 'bg-rose-500';
}

function heatColor(min) {
  if (!min) return 'bg-zinc-900';
  if (min < 30)  return 'bg-emerald-900/40';
  if (min < 120) return 'bg-emerald-700/60';
  if (min < 240) return 'bg-emerald-500/80';
  if (min < 360) return 'bg-emerald-400';
  return 'bg-emerald-300';
}

export default function KpiCockpitPage() {
  const { api, user } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get(`/admin/kpi-cockpit?days=${days}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load KPI data');
    } finally {
      setLoading(false);
    }
  }, [api, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allDays = useMemo(() => {
    if (!data?.users?.length) return [];
    const set = new Set();
    data.users.forEach(u => Object.keys(u.heatmap || {}).forEach(d => set.add(d)));
    return Array.from(set).sort();
  }, [data]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="kpi-cockpit-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-emerald-400" />
              KPI Cockpit
            </h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Studio-wide measured effort, sourced from VOS Studio Agent. Active = artist's hands on keyboard;
              Idle = window open but no input for 5+ min; Blender = time with Blender in foreground.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <select
              data-testid="kpi-cockpit-days"
              className="bg-zinc-900 border border-zinc-800 rounded-md text-[12px] px-2 py-1"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
            >
              <option value={1}>Today</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button
              onClick={fetchData}
              data-testid="kpi-cockpit-refresh"
              className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-zinc-900 border border-zinc-800 hover:bg-zinc-800"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 px-4 py-2 mb-6 text-[13px]">
            {error}
          </div>
        )}

        {/* Top stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-8">
          <StatCard icon={Activity} tint="emerald" label="Active hours" value={fmtH(data?.totals?.active_min)} hint="Hands-on work" />
          <StatCard icon={Clock} tint="amber" label="Idle hours" value={fmtH(data?.totals?.idle_min)} hint="App open, no input" />
          <StatCard icon={Cpu} tint="sky" label="Blender hours" value={fmtH(data?.totals?.blender_min)} hint="Time in Blender" />
          <StatCard icon={Users} tint="zinc" label="Artists tracked" value={data?.totals?.user_count ?? 0} hint="Workstations with samples" />
        </div>

        {/* Per-artist table */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold text-zinc-300 mb-3">Per-artist totals</h2>
          {!data || data.users.length === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-10 text-center">
              <Activity className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
              <p className="text-sm text-zinc-400">No activity samples yet.</p>
              <p className="text-[11px] text-zinc-600 mt-1">Once artists install the VOS Studio Agent and sign in, their work shows up here.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-zinc-800 overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-zinc-900/80 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Artist</th>
                    <th className="text-left px-4 py-2 font-medium">Role</th>
                    <th className="text-right px-4 py-2 font-medium">Active</th>
                    <th className="text-right px-4 py-2 font-medium">Blender</th>
                    <th className="text-right px-4 py-2 font-medium">Idle</th>
                    <th className="text-left px-4 py-2 font-medium w-1/4">Active ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.user_id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`kpi-row-${u.user_id}`}>
                      <td className="px-4 py-2">
                        <div className="text-zinc-200 font-medium">{u.full_name || u.email || u.user_id.slice(0, 8)}</div>
                        <div className="text-[10px] text-zinc-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-2 text-zinc-400 capitalize">{u.role}</td>
                      <td className="px-4 py-2 text-right text-emerald-300 font-mono">{fmtH(u.active_min)}</td>
                      <td className="px-4 py-2 text-right text-sky-300 font-mono">{fmtH(u.blender_min)}</td>
                      <td className="px-4 py-2 text-right text-amber-300 font-mono">{fmtH(u.idle_min)}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div className={`h-full ${ratioColor(u.ratio_active)}`} style={{ width: `${u.ratio_active * 100}%` }} />
                          </div>
                          <span className="text-[11px] text-zinc-400 font-mono w-10 text-right">{Math.round(u.ratio_active * 100)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Heatmap */}
        {data?.users?.length > 0 && allDays.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-zinc-300 mb-3">Day × Artist heat-map (active minutes)</h2>
            <div className="rounded-xl border border-zinc-800 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left px-3 py-2 sticky left-0 bg-zinc-950">Artist</th>
                    {allDays.map(d => (
                      <th key={d} className="text-center px-2 py-2 font-medium">{d.slice(5)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.users.map(u => (
                    <tr key={u.user_id}>
                      <td className="px-3 py-2 text-zinc-300 sticky left-0 bg-zinc-950 whitespace-nowrap">{u.full_name || u.email || u.user_id.slice(0, 8)}</td>
                      {allDays.map(d => {
                        const m = u.heatmap?.[d] || 0;
                        return (
                          <td key={d} className="p-0">
                            <div
                              className={`w-full h-7 ${heatColor(m)} hover:ring-1 hover:ring-emerald-400 transition-all`}
                              title={`${d}: ${fmtH(m)} active`}
                              data-testid={`heat-${u.user_id}-${d}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2 mt-3 text-[11px] text-zinc-500">
              <span>Less</span>
              {['bg-zinc-900', 'bg-emerald-900/40', 'bg-emerald-700/60', 'bg-emerald-500/80', 'bg-emerald-400', 'bg-emerald-300'].map((c, i) => (
                <span key={i} className={`w-5 h-3 ${c} rounded-sm`} />
              ))}
              <span>More</span>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, tint }) {
  const tints = {
    emerald: { bg: 'bg-emerald-500/10', ring: 'ring-emerald-500/30', text: 'text-emerald-300' },
    amber:   { bg: 'bg-amber-500/10',   ring: 'ring-amber-500/30',   text: 'text-amber-300' },
    sky:     { bg: 'bg-sky-500/10',     ring: 'ring-sky-500/30',     text: 'text-sky-300' },
    zinc:    { bg: 'bg-zinc-500/10',    ring: 'ring-zinc-500/30',    text: 'text-zinc-300' },
  };
  const t = tints[tint] || tints.zinc;
  return (
    <div className={`rounded-xl border border-zinc-800 ${t.bg} ring-1 ${t.ring} p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${t.text}`} />
        <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-[11px] text-zinc-500 mt-1">{hint}</p>
    </div>
  );
}
